import morphism, { createSchema } from 'morphism';

import { CONTACTLESS_VERIFICATION_TYPES } from '@hero/constants';

const settingSchema = {
  expiresAt: 'expiresAt',
  allowPremiumDates: 'allowPremiumDates',
  disableReGifting: 'disableReGifting',
  disableScratchEffect: 'disableScratchEffect',
  receiveThankYouNotes: 'receiveThankYouNotes',
  redeemRules: 'redeemRules',
  recipientVerification: 'recipientVerification'
};

const recognitionBaseSchema = {
  name: 'name',
  contacts: source =>
    source.contacts?.type
      ? {
          maxCount: source?.contacts?.maxCount > 0 ? Number(source.contacts.maxCount) : undefined,
          type: source.contacts.type
        }
      : undefined,
  products: source =>
    source.products?.length
      ? source.products.map(product => ({
          id: product.merchantProductId || product.id,
          images:
            (product?.images?.length &&
              product?.images?.map(image => ({
                default: image?.default,
                full: image?.full,
                thumb: image?.thumb,
                url: image?.url
              }))) ||
            undefined,
          source: product?.source || undefined,
          name: product?.name,
          brand: product?.brand,
          merchantProductId: product.merchantProductId,
          meta: {
            productType: product?.meta?.productType
          },
          prices: product.prices,
          skus: product.skus?.map(sku => ({
            code: sku.code,
            price: sku.price,
            priceBreakdown: sku.priceBreakdown
          }))
        }))
      : undefined,
  experienceSettings: source =>
    source.experienceSettings
      ? {
          ...source.experienceSettings,
          occasion: source.experienceSettings?.occasion || undefined,
          branding: source.experienceSettings?.branding || undefined,
          messageParams: Object.keys(source.experienceSettings?.messageParams || {}).length
            ? {
                message: source.experienceSettings.messageParams?.message || undefined,
                senderName: source.experienceSettings.messageParams?.senderName || undefined,
                aboutMessage: source.experienceSettings.messageParams?.aboutMessage || undefined,
                customizedRecipientMessages:
                  Object.keys(
                    source.experienceSettings.messageParams?.customizedRecipientMessages || {}
                  ).length > 0
                    ? source.experienceSettings.messageParams?.customizedRecipientMessages
                    : undefined
              }
            : undefined
        }
      : undefined,
  settings: source => morphism(settingSchema, source.settings),
  paymentMethod: source =>
    source?.paymentMethod
      ? {
          code: source.paymentMethod.code,
          name: source.paymentMethod.name,
          type: source.paymentMethod.type,
          meta: source.paymentMethod.meta
        }
      : undefined,
  meta: 'meta',
  addons: 'addons',
  flowCode: 'flowCode'
};

const create = createSchema(
  {
    ...recognitionBaseSchema
  },
  { undefinedValues: { strip: true } }
);

const update = createSchema(
  {
    ...recognitionBaseSchema,
    settings: source => {
      const acceptedSettings = morphism(settingSchema, source.settings);
      if (!acceptedSettings) {
        return undefined;
      }

      const sourceSettingsRecipientVerificationParams =
        acceptedSettings?.recipientVerification?.params;
      const { emailDomains, ...rest } = sourceSettingsRecipientVerificationParams || {};

      return {
        ...acceptedSettings,
        recipientVerification: acceptedSettings?.recipientVerification
          ? {
              ...acceptedSettings?.recipientVerification,
              params: {
                ...(rest || {}),
                ...(acceptedSettings.recipientVerification?.type ===
                  CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN && !!emailDomains?.length
                  ? {
                      emailDomains
                    }
                  : {})
              }
            }
          : undefined
      };
    },
    status: 'status'
  },
  { undefinedValues: { strip: true } }
);

const duplicate = createSchema(
  {
    name: 'name',
    options: {
      includeProducts: 'includeGifts',
      includeRecipients: 'includeRecipients',
      includeExperience: 'includeExperience'
    },
    meta: 'meta'
  },

  { undefinedValues: { strip: true } }
);

const calculateEstimatedCharge = createSchema(
  {
    recipientCount: 'recipientCount',
    products: source =>
      source.products.map?.(product => ({
        id: product?.merchantProductId,
        skus: product.skus.map(sku => ({
          code: sku.code
        })),
        meta: {
          productType: product?.meta?.productType
        }
      }))
  },

  { undefinedValues: { strip: true } }
);

export default {
  create: createSchema(create, { undefinedValues: { strip: true } }),
  update: createSchema(update, { undefinedValues: { strip: true } }),
  duplicate: createSchema(duplicate, { undefinedValues: { strip: true } }),
  calculateEstimatedCharge: createSchema(calculateEstimatedCharge, {
    undefinedValues: { strip: true }
  })
};
